import { useState, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useLocation } from 'react-router'
import { Helmet } from 'react-helmet-async'
import { parse } from 'query-string'
import { Columns, Column, Hidden, Box, Inline, useDialogController } from '@machobear/design-system'
import { useDispatch } from 'react-redux'
import config from '@/config'
import PageTitle from '@/components/page-title'
import { structure } from '@/styles'
import { PaymentMethod, PaymentSummary, LogoIcon } from '@/features/Promotion'
import Dropdown from '@/components/header/dropdown'
import NavDropDown from '@/components/header/nav-drop-down'
import WarningModal from '@/components/modals/warning-modal'
import { WarningLarge, CircleErrorAltLargeIcon } from '@/components/icons'
import useRole from '@/hooks/use-role'
import useGetPromotions from '@/hooks/use-promotions'
import { Product } from '@/features/Promotion/types'

const { WEB_URL } = config

type Data = {
  products: Product[]
  addons?: Product
}
type Props = {
  match: { params: { promotionId: string } }
}

const ERROR_KEYS = [
  'err.billing_user_not_set',
  'err.generic.not_found',
  'payment_method_not_available'
]

const Promotions = ({
  match: {
    params: { promotionId }
  }
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const [setReferenceElement] = useState(null)
  const [state, setState] = useState<string | null>(null)
  const [isAddOnsStep, setIsAddOnsStep] = useState(false)
  const [addOnItems, setAddOnItems] = useState<Product>({
    items: [],
    total_before_tax: '',
    total_after_tax: '',
    total_tax: '',
    total_due: '',
    discount_from_regular_price: '',
    metadata: { package_name: '' }
  })
  const [addOnsLoading, setAddOnsLoading] = useState(false)
  const { visible, showModal, closeModal } = useDialogController(false)

  const [isBillingUserWarningModalOpen, setIsBillingUserWarningModalOpen] = useState(false)
  const role = useRole()

  const couponCode = parse(location.search)?.couponCode as string | null

  const { data, isLoading, error }: { data: Data; isLoading: boolean; error: any } =
    useGetPromotions({
      country: 'CA',
      province: state ?? 'BC',
      promotionId,
      couponCode
    })

  const logout = () => dispatch.auth.logout()
  const redirectToDefaultPath = () => window.location.replace(`/listings`)
  const title = t('promotions.title', 'Promotions')
  const ctaText = t('common.log_out', 'Log Out')
  const billingUserWarningModalCtaText = t(
    t('r.landlord.view.logged_in_as_renter.modal.cta', 'Got it!')
  )
  const errorMessage = error?.message
  const errorMessageKey = error?.message_key
  const renterWarningModalTitle = t(
    'r.landlord.view.logged_in_as_renter.title',
    "Oops! It looks like you're logged in as a renter."
  )
  const billingUserWarningModalTitle = t(
    'r.landlord.view.logged_in_as_non_billing_user.title',
    'Oops! Something went wrong.'
  )

  const renterWarningModalDescription = (
    <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column' }}>
      <span>
        {t(
          'r.landlord.view.logged_in_as_renter.text',
          'To access landlord-specific features, please log out and sign up for a landlord account or log in with your landlord account details.'
        )}
      </span>
      <br />
      <span>
        <Trans i18nKey="r.landlord.view.logged_in_as_renter.text_3">
          If you made this account in error, you can contact us at{' '}
          <a href="mailto:support@liv.rent" target="_blank" rel="noopener noreferrer">
            support@liv.rent
          </a>
          .
        </Trans>
      </span>
    </div>
  )

  const billingUserWarningModalDescription = (
    <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column' }}>
      <span>
        <Trans
          i18nKey="r.landlord.view.logged_in_non_billing_user.text_3"
          errorMessage={errorMessage}
        >
          {{ errorMessage }} If the issue persists, please contact{' '}
          <a href="mailto:support@liv.rent" target="_blank" rel="noopener noreferrer">
            support@liv.rent
          </a>
          .
        </Trans>
      </span>
    </div>
  )

  const product = data?.products?.[0]

  const addOns = data?.addons
  const summaryData = addOnItems?.items?.length > 0 ? addOnItems : product

  useEffect(() => {
    if (role === 'tenant') {
      showModal()
    }
  }, [role])

  useEffect(() => {
    if (!ERROR_KEYS.includes(errorMessageKey) && errorMessage)
      window.location.replace(`${WEB_URL}/404`)
  }, [errorMessage, errorMessageKey])

  useEffect(() => {
    ERROR_KEYS.includes(errorMessageKey) && setIsBillingUserWarningModalOpen(true)
  }, [errorMessageKey])
  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <PageTitle hideDivider>{title}</PageTitle>
      <WarningModal
        visible={visible}
        title={renterWarningModalTitle}
        description={renterWarningModalDescription}
        illustration={<WarningLarge />}
        ctaText={ctaText}
        ctaAction={logout}
        onClose={closeModal}
        hideXButton={true}
      />
      <WarningModal
        visible={isBillingUserWarningModalOpen}
        title={billingUserWarningModalTitle}
        description={billingUserWarningModalDescription}
        illustration={<CircleErrorAltLargeIcon />}
        ctaText={billingUserWarningModalCtaText}
        ctaAction={redirectToDefaultPath}
        onClose={() => setIsBillingUserWarningModalOpen(false)}
        hideXButton={true}
      />

      <Box
        style={{
          top: structure.header.heightMobile
        }}
      >
        <Hidden above="mobile">
          <Box background="white" width="full" paddingY="xsmall" paddingX="medium">
            <Columns space="none" alignY="center">
              <Column width="1/2">
                <Inline align="left" space="none">
                  <LogoIcon />
                </Inline>
              </Column>
              <Column width="1/2">
                <Inline align="right" space="none">
                  <Box style={{ position: 'relative' }}>
                    <NavDropDown
                      isOpen={isOpen}
                      setIsOpen={setIsOpen}
                      setReferenceElement={setReferenceElement}
                      width="139px"
                    />
                    {isOpen && (
                      <Dropdown isOpen={isOpen} setIsOpen={setIsOpen} forPromotion={true} />
                    )}
                  </Box>
                </Inline>
              </Column>
            </Columns>
          </Box>
        </Hidden>
        <Columns collapseBelow="tablet" space="none" alignY="top">
          <Column width="1/2">
            <PaymentSummary
              data={summaryData}
              bundleName={product?.metadata?.package_name}
              isLoading={isLoading || addOnsLoading}
              state={state ?? 'BC'}
              promotionId={promotionId}
              couponCode={couponCode}
            />
          </Column>
          <Column width="1/2">
            <PaymentMethod
              isAddOnsStep={isAddOnsStep}
              setAddOnsLoading={setAddOnsLoading}
              addOnsLoading={addOnsLoading}
              setIsAddOnsStep={setIsAddOnsStep}
              setAddOnItems={setAddOnItems}
              addOns={addOns}
              data={summaryData}
              isLoading={isLoading}
              state={state}
              setState={setState}
              promotionId={promotionId}
              couponCode={couponCode}
              existingItems={product?.items}
              bundleName={product?.metadata?.package_name}
              price={product?.total_before_tax}
            />
          </Column>
        </Columns>
      </Box>
    </>
  )
}

export default Promotions
